import { useCallback, useEffect, useMemo, useState } from 'react';
import { payAllSalaries } from './services/salaryActions';
import {
  apiErrorMessage,
  fetchEmployees,
  fetchTransactions,
} from './utils/api';
import { fetchContractBalance } from './utils/fetchContractBalance';
import { fetchUsdBalance } from './utils/fetchUsdBalance';
import { centsToAmount, formatCents, totalPayoutCents } from './utils/amounts';
import Loader from './components/loader/Loader';
import ConnectButton from './components/connect/ConnectButton';
import PaymentComponent from './components/payment/PaymentComponent';
import EmployeeTable from './components/employee/EmployeeTable';
import SearchComponent from './components/search/SearchComponent';
import Header from './components/header/Header';
import AddEmployeeButton from './components/addEmployee/AddEmployeeButton';
import PayoutConfirm from './components/confirm/PayoutConfirm';
import AddEmployeeModal from './modals/addEmployee/AddEmployeeModal';
import EditEmployeeModal from './modals/editEmployee/EditEmployeeModal';
import TransactionsModal from './modals/transactions/TransactionsModal';
import NotificationsProvider, {
  useNotify,
} from './components/notifications/NotificationsProvider';
import WalletProvider from './components/wallet/WalletProvider';
import { Employee } from './interfaces/Employee';
import { Transaction } from './interfaces/Transaction';
import './App.css';
import { ADMIN, HELPER } from './constants/constants';

const Panel = () => {
  const notify = useNotify();
  // PaymentComponent
  const [contractBalance, setContractBalance] = useState<number | null>(null);
  const [tokenBalance, setTokenBalance] = useState<string>('Загрузка...');
  const [usdBalance, setUsdBalance] = useState<string>('Загрузка...');
  const [usdHelperBalance, setUsdHelperBalance] =
    useState<string>('Загрузка...');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // SearchComponent
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchWalletQuery, setSearchWalletQuery] = useState<string>('');
  // Модальные окна
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showTxsModal, setShowTxsModal] = useState<boolean>(false);
  const [showPayoutConfirm, setShowPayoutConfirm] = useState<boolean>(false);

  const totalCents = useMemo(() => totalPayoutCents(employees), [employees]);

  const getEmployees = useCallback(async () => {
    try {
      setEmployees(await fetchEmployees());
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      notify.error(
        `Не удалось загрузить сотрудников: ${apiErrorMessage(error)}`,
      );
    }
  }, [notify]);

  const getContractBalance = useCallback(async () => {
    try {
      const balance = Number(await fetchContractBalance());
      setContractBalance(balance);
      setTokenBalance(String(Math.floor(balance)));
    } catch (error) {
      console.error('Failed to fetch balance:', error);
      setContractBalance(null);
      setTokenBalance('Ошибка');
    }
  }, []);

  const getUsdBalance = useCallback(
    async (wallet: string, set: (value: string) => void) => {
      try {
        set(String((await fetchUsdBalance(wallet)) ?? '0'));
      } catch (error) {
        console.error(`Failed to fetch USD balance of ${wallet}:`, error);
        set('Ошибка');
      }
    },
    [],
  );

  // После любого изменения данных — перечитать, а не перезагружать страницу:
  // перезагрузка сбрасывала подключение кошелька и поиск.
  const refresh = useCallback(() => {
    getEmployees();
    getContractBalance();
  }, [getContractBalance, getEmployees]);

  useEffect(() => {
    refresh();
    getUsdBalance(ADMIN, setUsdBalance);
    getUsdBalance(HELPER, setUsdHelperBalance);
  }, [getUsdBalance, refresh]);

  const handlePaySalaries = async () => {
    setShowPayoutConfirm(false);
    setLoading(true);

    try {
      await payAllSalaries(centsToAmount(totalCents));
      notify.success(`Зарплаты выплачены: ${formatCents(totalCents)} EMIVN`);
      refresh();
    } catch (error) {
      notify.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const getTransactions = async () => {
    setShowTxsModal(true);

    try {
      setTransactions(await fetchTransactions());
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      notify.error(`Не удалось загрузить выплаты: ${apiErrorMessage(error)}`);
    }
  };

  const openEditModal = (wallet: string) => {
    setEditing(
      employees.find((employee) => employee.wallet_address === wallet) ?? null,
    );
  };

  return (
    <div className="container">
      <Header />

      <PaymentComponent
        totalPayout={formatCents(totalCents)}
        tokenBalance={tokenBalance}
        paySalaries={() => setShowPayoutConfirm(true)}
        getTransactions={getTransactions}
        usdBalance={usdBalance}
        usdHelperBalance={usdHelperBalance}
      />

      <div className="top-bar">
        <SearchComponent
          searchQuery={searchQuery}
          searchWalletQuery={searchWalletQuery}
          onSearchChange={setSearchQuery}
          onSearchWalletChange={setSearchWalletQuery}
        />

        <div className="actions">
          <AddEmployeeButton onClick={() => setShowAddModal(true)} />
          <ConnectButton />
        </div>
      </div>

      <EmployeeTable
        employees={employees}
        searchQuery={searchQuery}
        searchWalletQuery={searchWalletQuery}
        onEdit={openEditModal}
      />

      {showAddModal && (
        <AddEmployeeModal
          employees={employees}
          onClose={() => setShowAddModal(false)}
          onChanged={refresh}
        />
      )}

      {editing && (
        <EditEmployeeModal
          employee={editing}
          onClose={() => setEditing(null)}
          onChanged={refresh}
        />
      )}

      {showTxsModal && (
        <TransactionsModal
          transactions={transactions}
          onClose={() => setShowTxsModal(false)}
        />
      )}

      {showPayoutConfirm && (
        <PayoutConfirm
          employees={employees}
          totalCents={totalCents}
          contractBalance={contractBalance}
          onConfirm={handlePaySalaries}
          onCancel={() => setShowPayoutConfirm(false)}
        />
      )}

      {loading && <Loader />}
    </div>
  );
};

const App = () => (
  <NotificationsProvider>
    <WalletProvider>
      <Panel />
    </WalletProvider>
  </NotificationsProvider>
);

export default App;
